import { useCallback, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import { CampaignSummary } from "@signalist/shared-types";

import { UpdateType } from "@/enum/list.enum";
import { useLazyGetListQuery, useUpdateListMutation } from "@/pages/Private/redux/list/list.api";

import { Campaign } from "../../List/schema/campaign";
import {
	buildClonedCampaignConfig,
	ClonableCampaign,
} from "../../List/components/CampaignOverview/utils/buildClonedCampaignConfig.util";
import { eventStopDatePrefill } from "../../List/components/CampaignOverview/utils/eventStopDatePrefill.util";

/** One selected campaign row on the Campaigns overview (a campaign always belongs to one list). */
export interface SelectedCampaign {
	listId: number;
	campaign: CampaignSummary;
}

/** The selected campaigns of each list, for per-list writes (duplicate merges one config PATCH). */
const groupByList = (selection: SelectedCampaign[]): Map<number, CampaignSummary[]> => {
	const byList = new Map<number, CampaignSummary[]>();

	for (const { listId, campaign } of selection) {
		byList.set(listId, [...(byList.get(listId) ?? []), campaign]);
	}

	return byList;
};

/**
 * Bulk actions of the Campaigns overview selection bar (PREC-3880): duplicate clones each selected
 * campaign into its list's `campaignConfig` (senders stripped, `isValid: false` — the clone needs
 * review before it can run), one PATCH per affected list.
 *
 * Bulk **resume / pause was removed** (PREC-4129). It fanned out to the per-campaign status
 * endpoint across every list in the selection, and it silently dropped every campaign that was not
 * exactly PAUSED (resume) or ACTIVE (pause) — so a READY campaign the user had ticked produced no
 * request, no error and no message, while a paused sibling on a different list was started instead.
 * A BlueOak user hit that on 2026-09-10: the campaign he selected never started, a dormant one from
 * April did, and it sent 18 LinkedIn messages before he noticed. Status changes now go through the
 * per-campaign pill, which acts on exactly the campaign the user pointed at and reports its own
 * failures. Do not reinstate a bulk status action without telling the user, before the click, how
 * many of the selected campaigns it will actually touch and which ones it will skip.
 */
export const useCampaignBulkActions = ({
	refreshList,
	onDone,
}: {
	/** Re-fetch one list's campaign summaries after a mutation (from useListCampaignSummaries). */
	refreshList: (listId: number) => Promise<void>;
	/** Called after an action completes — the table clears the selection here. */
	onDone: () => void;
}) => {
	const [updateList] = useUpdateListMutation();
	const [getList] = useLazyGetListQuery();
	const [isPending, setIsPending] = useState(false);

	const duplicateSelected = useCallback(
		async (selection: SelectedCampaign[]) => {
			if (selection.length === 0) {
				return;
			}

			setIsPending(true);

			try {
				for (const [listId, campaigns] of groupByList(selection)) {
					// The lean rows carry no campaignConfig — fetch the full list once per affected list.
					const list = await getList(listId).unwrap();
					const config = (list.campaignConfig ?? {}) as Record<string, Campaign>;
					let clones: Record<string, ClonableCampaign> = {};

					for (const campaign of campaigns) {
						const source = config[campaign.campaignId];

						if (source) {
							clones = buildClonedCampaignConfig(
								clones,
								source,
								uuidv4(),
								eventStopDatePrefill(list.event)
							);
						}
					}

					if (Object.keys(clones).length === 0) {
						continue;
					}

					// Only the clones travel, as a merge (PREC-4026): replacing the whole map from a
					// fetched snapshot could still lose an edit that landed in between.
					await updateList({
						id: listId,
						updateType: UpdateType.CAMPAIGN_CONFIG_MERGE,
						campaignConfig: JSON.stringify(clones),
						campaignWriteOrigin: "bulk-duplicate",
					}).unwrap();

					await refreshList(listId);
				}
			} catch {
				// Surfaced by the global RTK Query error middleware.
			} finally {
				setIsPending(false);
				onDone();
			}
		},
		[getList, updateList, refreshList, onDone]
	);

	return { duplicateSelected, isPending };
};
